"use client"
import React, {useState} from "react"
import {Button} from "@/components/ui/button"
import {Loader, MinusIcon, PlusIcon, Trash2} from "lucide-react"
import {useCart} from "@/store/shipping-cart/useCart"
import {toast} from "react-hot-toast"
import {SingleProductEnum} from "@/constants/commerce-enum"
import {CartItem} from "@/store/shipping-cart/cart-store";
import {DataLayerFactory} from "@/components/google-tag-manager/DataLayerFactory";

interface Product {
    id: string | number
    total_quantity?: number | any
    unique_id: string;
    max_basket_limit: number;
    min_total_qty_and_max_basket_qty: number;
}

interface AddToBasketProps {
    product: Product | any
    price?: number
}

function AddToBasketProductCard({product, price}: AddToBasketProps) {
    const {addToCart, removeFromCart, updateItemQuantity, items = []} = useCart()
    const [isAdding, setIsAdding] = useState(false)

    const filteredItem: CartItem | any = items?.find(item => item?.unique_id === product?.unique_id);
    const quantity = filteredItem?.quantity ?? null
    const limit = Math.min(product?.max_basket_limit, product?.min_total_qty_and_max_basket_qty);

    const handleAddToCart = async () => {
        try {
            setIsAdding(true)
            addToCart(product)
            toast.success(SingleProductEnum.addToBasketSuccessfully, {
                position: "top-center",
            })
            const addToCartDataLayer = DataLayerFactory.make('add_to_cart', 0, '', product?.product_item_id, 1 , '' );
            addToCartDataLayer.pushToDataLayer();
        } catch (error) {
            toast.error("خطا در افزودن به سبد خرید")
            console.error("Add to cart error:", error)
        } finally {
            setIsAdding(false)
        }
    }

    const handleIncrease = () => {
        if (quantity < limit) {
            updateItemQuantity(product.unique_id, quantity + 1)
        } else {

            toast.error(`بیشتراز ${limit} عدد نمیتوانید به سبد خرید خود اضافه کنید`, {
                position: "top-center",
            });
            return;
        }
    }

    const handleDecrease = () => {
        if (quantity > 1) {
            updateItemQuantity(product.unique_id, quantity - 1)
        } else {
            removeFromCart(product.unique_id)
        }
    }

    const isDisabled = isAdding || quantity >= limit

    if (quantity > 0) {
        return (
            <div className="flex flex-row-reverse items-center gap-0.5  border-orange-primary ">
                <Button
                    onClick={handleDecrease}
                    aria-label="decrease-quantity"
                    variant={"outline"}
                    className="p-0.5 w-6 h-6 md:h-8 md:w-8 rounded-lg m-0"
                >
                    {quantity < 2 ? <Trash2 className={"w-4 h-4 text-orange-primary"}/> : <MinusIcon/>}
                </Button>
                <span className="px-1 text-orange-primary font-medium">{quantity.toLocaleString("fa")}</span>
                <Button
                    onClick={handleIncrease}
                    aria-label="increase-quantity"
                    variant={"outline"}
                    className="p-0.5 w-6 h-6 md:h-8 md:w-8 rounded-lg m-0"
                    disabled={isDisabled}
                >
                    <PlusIcon/>
                </Button>
            </div>
        )
    }

    return (
        <Button
            onClick={handleAddToCart}
            aria-label="add-product-to-cart"
            variant="outline"
            className="p-0.5 w-6 h-6 md:h-8 md:w-8 rounded-lg m-0"
        >
            {isAdding ? (
                <Loader className="w-6 h-6 md:w-8 md:h-8 animate-spin"/>
            ) : (
                <PlusIcon/>
            )}
        </Button>
    )
}

export default AddToBasketProductCard

